import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type * as PanelTypes from '../../state/views/panel/types';
import type * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {INLINE_WORKSPACE_DRAWER_DURATION} from './constants';

export type DrawerContentType =
  | 'add-panels'
  | 'create-trigger'
  | 'panels-quick-add'
  | 'section-settings'
  | 'workspace-settings'
  | 'media-settings'
  | 'evaluation-tables-settings'
  | 'run-issues'
  | 'line-plot-editor'
  | 'marimo-settings'
  | undefined;

export type WorkspaceDrawerContextType = {
  contentType: DrawerContentType;
  delayedContentType: DrawerContentType;
  targetSectionRef?: PanelBankSectionConfigTypes.Ref;
  closeWorkspaceDrawer: (shouldAnimate?: boolean) => void;
  openWorkspaceDrawer: (
    type: DrawerContentType,
    sectionRef?: PanelBankSectionConfigTypes.Ref,
    panelRef?: PanelTypes.Ref
  ) => void;
  targetPanelRef?: PanelTypes.Ref;
  toggleWorkspaceDrawer: (
    type: DrawerContentType,
    sectionRef?: PanelBankSectionConfigTypes.Ref,
    panelRef?: PanelTypes.Ref
  ) => void;
};

export const WorkspaceDrawerContext = createContext<
  WorkspaceDrawerContextType | undefined
>(undefined);

type WorkspaceDrawerProps = {
  children: React.ReactNode;
};

/**
 * This context provider is used to control the drawer that slides in and out
 * in a workspace page.
 */
export const WorkspaceDrawerContextProvider = ({
  children,
}: WorkspaceDrawerProps) => {
  const [contentType, setContentType] = useState<DrawerContentType>();
  const [delayedContentType, setDelayedContentType] =
    useState<DrawerContentType>();

  const [targetSectionRef, setTargetSectionRef] = useState<
    PanelBankSectionConfigTypes.Ref | undefined
  >();

  const [targetPanelRef, setTargetPanelRef] = useState<
    PanelTypes.Ref | undefined
  >();

  const openWorkspaceDrawer = useCallback(
    (
      type: DrawerContentType,
      sectionRef?: PanelBankSectionConfigTypes.Ref,
      panelRef?: PanelTypes.Ref
    ) => {
      setContentType(type);
      setTargetSectionRef(sectionRef);
      setTargetPanelRef(panelRef);
    },
    []
  );

  const closeWorkspaceDrawer = useCallback(() => {
    setContentType(undefined);
  }, []);

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (contentType) {
          closeWorkspaceDrawer();
        }
      }
    }
    // TODO: migrate workspace drawer Escape handling to the inputSystem
    // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [closeWorkspaceDrawer, contentType]);

  const toggleWorkspaceDrawer = useCallback(
    (
      newContentType: DrawerContentType,
      newTargetSectionRef?: PanelBankSectionConfigTypes.Ref,
      newPanelRef?: PanelTypes.Ref
    ) => {
      if (
        newContentType !== contentType ||
        newTargetSectionRef?.id !== targetSectionRef?.id ||
        newPanelRef?.id !== targetPanelRef?.id
      ) {
        openWorkspaceDrawer(newContentType, newTargetSectionRef, newPanelRef);
      } else {
        closeWorkspaceDrawer();
      }
    },
    [
      contentType,
      targetSectionRef?.id,
      targetPanelRef?.id,
      openWorkspaceDrawer,
      closeWorkspaceDrawer,
    ]
  );

  // Delay state updates to account for open/close animation
  useEffect(() => {
    const timeout = setTimeout(
      () => {
        setDelayedContentType(contentType);
        if (!contentType) {
          setTargetSectionRef(undefined);
          setTargetPanelRef(undefined);
        }
      },
      INLINE_WORKSPACE_DRAWER_DURATION + 100 // HAX: a buffer helps with jumpiness
    );

    return () => clearTimeout(timeout);
  }, [contentType]);

  const value = useMemo(
    () => ({
      closeWorkspaceDrawer,
      contentType,
      delayedContentType,
      openWorkspaceDrawer,
      targetSectionRef,
      targetPanelRef,
      toggleWorkspaceDrawer,
    }),
    [
      closeWorkspaceDrawer,
      contentType,
      delayedContentType,
      openWorkspaceDrawer,
      targetSectionRef,
      targetPanelRef,
      toggleWorkspaceDrawer,
    ]
  );

  return (
    <WorkspaceDrawerContext.Provider value={value}>
      {children}
    </WorkspaceDrawerContext.Provider>
  );
};

export const useMaybeWorkspaceDrawerContext = () => {
  return useContext(WorkspaceDrawerContext);
};

export const useWorkspaceDrawerContext = (): WorkspaceDrawerContextType => {
  const value = useContext(WorkspaceDrawerContext);

  if (value == null) {
    throw new Error(
      'useWorkspaceDrawerContext must be used within a WorkspaceDrawerContextProvider'
    );
  }

  return value;
};

export const useWorkspaceDrawerContextMaybe = () => {
  return useContext(WorkspaceDrawerContext);
};
